import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './config';
import { TokenManager } from './tokenManager';
import { HTTP_CONFIG } from '../constants/http';

export const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: HTTP_CONFIG.TIMEOUT_MS,
});

// Request interceptor - add auth token
httpClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const existingToken = TokenManager.getAccessToken();
  if (existingToken && TokenManager.isAccessTokenExpired()) {
    try {
      await TokenManager.refreshTokens();
    } catch (refreshError) {
      return Promise.reject(new Error('Token expired and refresh failed'));
    }
  }

  const token = TokenManager.getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Define interface for retry tracking
interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Response interceptor - handle 401s
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = error.config as RetryableRequest;

    const isRefreshCall = originalRequest?.url?.includes('/refresh');
    const isAuthEndpoint = originalRequest?.url?.match(/\/(login|register|verify)/);

    if (status === 401 && !isRefreshCall && !isAuthEndpoint && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await TokenManager.refreshTokens();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return httpClient(originalRequest);
      } catch (refreshErr) {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);
